"use client";
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useSelector, useDispatch } from "react-redux";

import { App } from "antd";
import bsImage from "@/assets/images/bs.png";
import cnamLogo from "@/assets/images/cnam1.jpg";
import { useMedicalActs, useCnssDependents } from "../../../../consultation/hooks/useCnss";
import "./BulletinDeSoins.css";
import { getCurrentDoctor } from "../../../../authDoctor/redux/thunks/doctorsThunks";
import useConsultations from "../../../../consultation/hooks/useConsultations";

/* ====== helpers ====== */

function Th({ children, className = "", style = {} }) {
  return (
    <th className={`bs-th ${className}`} style={style}>
      {children}
    </th>
  );
}

function EmptyRows({ cols, rows = 5 }) {
  return Array.from({ length: rows }).map((_, r) => (
    <tr key={r}>
      {Array.from({ length: cols }).map((_, c) => (
        <td key={c} className={r % 2 === 1 ? "bs-td bs-td-even" : "bs-td"}>
          <input className="bs-input" />
        </td>
      ))}
    </tr>
  ));
}

/* Aucune cellule n'est verrouillee : le medecin doit pouvoir corriger son
   code conventionnel / matricule fiscal (ils restent memorises pour le
   prochain BS) comme n'importe quelle autre case. Avant, des que les deux
   etaient saisis une fois, `lockedDoctorInfo` passait a true et les rendait
   definitivement readOnly — et sur les tableaux a 5 colonnes (biologie,
   pharmacie) le verrou tombait d'ailleurs sur les mauvaises colonnes :
   il bloquait "CACHET ET SIGNATURE" et laissait le code modifiable. */
function RectoEditableRows({ rows, onChange }) {
  return rows.map((row, r) => (
    <tr key={r}>
      {row.map((cell, c) => (
        <td key={c} className="recto-td">
          <input
            className="recto-input"
            value={cell}
            onChange={(e) => onChange(r, c, e.target.value)}
          />
        </td>
      ))}
    </tr>
  ));
}

function splitIdentifiant(identifiant, length = 10) {
  if (!identifiant) return Array(length).fill("");
  const s = String(identifiant).replace(/\D/g, "").slice(0, length);
  const arr = Array(length).fill("");
  for (let i = 0; i < s.length && i < length; i += 1) arr[i] = s[i];
  return arr;
}

function splitAdresseEtCodePostal(rawAdresse) {
  if (!rawAdresse) return { adresse: "", codePostal: "" };
  const s = String(rawAdresse).trim();
  const m = s.match(/^(.*?)(\d{4})\s*$/);
  if (!m) return { adresse: s, codePostal: "" };
  const adresse = m[1].trim().replace(/[,\s]+$/, "");
  const codePostal = m[2];
  return { adresse, codePostal };
}

/* ====== VERSO - gauche ====== */

function FaceGauche() {
  return (
    <div className="bs-face-col">
      <div className="bs-green-hdr">
        CONSULTATIONS ET ACTES DE SOINS DENTAIRES
      </div>
      <div className="bs-sub-hdr">
        Il est indispensable d'indiquer la dent traitée, de désigner les actes
        pratiqués en se référant aux codes et cotations de la nomenclature officielle
      </div>
      <table className="bs-table">
        <thead>
          <tr>
            <Th style={{ width: 36 }}>DATE</Th>
            <Th style={{ width: 28 }}>DENT</Th>
            <Th>CODE ACTE</Th>
            <Th style={{ width: 32 }}>COTA-<br />TION</Th>
            <Th style={{ width: 38 }}>HONOR-<br />AIRES</Th>
            <Th style={{ width: 46 }}>CODE Profes-<br />sionnel de santé</Th>
            <Th style={{ width: 46 }}>CACHET ET<br />SIGNATURE</Th>
          </tr>
        </thead>
        <tbody>
          <EmptyRows cols={7} rows={8} />
        </tbody>
      </table>
      <div className="bs-dental-box">
        <img src={bsImage} alt="Schéma dentaire CNAM" className="bs-dental-img" />
      </div>
      <div className="bs-green-hdr">PROTHÈSES DENTAIRES</div>
      <table className="bs-table">
        <thead>
          <tr>
            <Th style={{ width: 36 }}>DATE</Th>
            <Th style={{ width: 30 }}>DENTS</Th>
            <Th>CODE ACTE</Th>
            <Th style={{ width: 32 }}>COTA-<br />TION</Th>
            <Th style={{ width: 38 }}>HONOR-<br />AIRES</Th>
            <Th style={{ width: 46 }}>CODE Profes-<br />sionnel de santé</Th>
            <Th style={{ width: 46 }}>CACHET ET<br />SIGNATURE</Th>
          </tr>
        </thead>
        <tbody>
          <EmptyRows cols={7} rows={6} />
        </tbody>
      </table>
    </div>
  );
}

/* ====== VERSO - droite ====== */

function FaceDroite({ card, patientId }) {
  const { allDependents, fetchByPatient } = useCnssDependents();
  const [maladeType, setMaladeType] = useState("");
  const [maladePrenom, setMaladePrenom] = useState("");
  const [maladeNom, setMaladeNom] = useState("");
  const [maladeDateNaissance, setMaladeDateNaissance] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedMalade, setSelectedMalade] = useState(null);

  useEffect(() => {
    if (patientId) fetchByPatient(patientId);
  }, [patientId, fetchByPatient]);

  /* Les champs de l'assure etaient `readOnly` : le medecin ne pouvait ni
     completer ni corriger une carte incomplete. On garde le prremplissage
     depuis la carte CNAM, mais la saisie reste possible. Le state est
     resynchronise uniquement quand la carte change (autre patient). */
  const derivedAssure = useMemo(() => {
    const fullName = String(card?.nomPrenom ?? "").trim();
    const parts = fullName.split(" ");
    const { adresse, codePostal } = splitAdresseEtCodePostal(card?.adresse ?? "");
    return {
      identifiant: splitIdentifiant(card?.identifiantUnique),
      lastName: parts.length > 0 ? parts[0] : "",
      firstName: parts.length > 1 ? parts.slice(1).join(" ") : "",
      adresse,
      codePostal,
    };
  }, [card]);

  const [assure, setAssure] = useState(derivedAssure);
  useEffect(() => { setAssure(derivedAssure); }, [derivedAssure]);

  const setAssureField = (key, value) =>
    setAssure((prev) => ({ ...prev, [key]: value }));

  const setIdentifiantCell = (index, value) =>
    setAssure((prev) => {
      const identifiant = [...prev.identifiant];
      identifiant[index] = String(value).replace(/\D/g, "").slice(-1);
      return { ...prev, identifiant };
    });

  const identifiantCells = assure.identifiant;
  // seuls prenom/nom sont relus directement (pre-remplissage du malade
  // quand le patient EST l'assure) ; les autres champs passent par assure[field]
  const { firstName, lastName } = assure;

  const maladeTypeToBackend = {
    assure: "ASSURE",
    conjoint: "ADJOINT",
    enfant: "ENFANT",
    ascendant: "KAFALA",
  };

  const selectedBackendType = maladeTypeToBackend[maladeType];

  const filteredDependents = useMemo(() => {
    if (!selectedBackendType || selectedBackendType === "ASSURE") return [];
    const search = `${maladePrenom} ${maladeNom}`.trim().toLowerCase();
    return (allDependents || [])
      .filter((dep) => dep.lienParente === selectedBackendType)
      .filter((dep) => {
        if (!search) return true;
        const name = String(dep.nomPrenom || "").toLowerCase();
        return name.startsWith(search) || name.includes(search);
      });
  }, [allDependents, selectedBackendType, maladePrenom, maladeNom]);

  const formatBirthDate = (value) => (!value ? "" : String(value).slice(0, 10));

  const fillMaladeFromDependent = (dep) => {
    const nomPrenom = String(dep.nomPrenom || "").trim();
    const nameParts = nomPrenom.split(" ");
    setSelectedMalade(dep);
    setMaladeNom(nameParts[0] || "");
    setMaladePrenom(nameParts.slice(1).join(" ") || "");
    setMaladeDateNaissance(formatBirthDate(dep.dateNaissance));
    setShowSuggestions(false);
  };

  const handleMaladeTypeChange = (type) => {
    setMaladeType(type);
    setSelectedMalade(null);
    setMaladePrenom("");
    setMaladeNom("");
    setMaladeDateNaissance("");
    if (type === "assure") {
      setMaladePrenom(firstName);
      setMaladeNom(lastName);
      setMaladeDateNaissance(card?.dateNaissance ? String(card.dateNaissance).slice(0, 10) : "");
      setSelectedMalade({ type: "ASSURE", nomPrenom: card?.nomPrenom || "", dateNaissance: card?.dateNaissance || "" });
    }
  };

  return (
    <div className="bs-face-droite">
      <div className="bs-watermark-bg">BS</div>
      <div className="bs-header">
        <div className="bs-logo-block">
          <img src={cnamLogo} alt="CNAM" className="bs-cnam-logo" />
        </div>
        <div className="bs-title-box">
          <div className="bs-title-ar">بطاقة استرجاع مصاريف علاج</div>
          <div className="bs-title-main">BULLETIN DE REMBOURSEMENT</div>
          <div className="bs-title-sub">DES FRAIS DE SOINS</div>
        </div>
        <div className="bs-ref-box">
          <div className="bs-ref-label">Réf. Dossier</div>
          <input className="bs-ref-input" />
        </div>
      </div>

      <div className="bs-section-title">
        <span>A REMPLIR PAR L'ASSURE SOCIAL</span>
        <span dir="rtl">يُعبَّر من طرف المضمون الاجتماعي</span>
      </div>
      <div className="bs-block">
        <div className="bs-id-row">
          <span className="bs-label-bold">IDENTIFIANT<br />UNIQUE</span>
          <div className="bs-id-grid">
            {identifiantCells.map((val, i) => (
              <input
                key={i}
                className="bs-id-cell"
                maxLength={1}
                value={val}
                inputMode="numeric"
                onChange={(e) => setIdentifiantCell(i, e.target.value)}
              />
            ))}
          </div>
          <span className="bs-ar-label">المعرّف الوحيد</span>
        </div>
        <div className="bs-check-row">
          <span className="bs-check-label">CNSS</span>
          <div className={`bs-small-box ${card ? "bs-small-box-checked" : ""}`} />
          <span className="bs-check-label">CNRPS</span>
          <div className="bs-small-box" />
          <span className="bs-check-label">Convention bilatérale</span>
          <div className="bs-small-box" />
        </div>
      </div>

      <div className="bs-section-title">
        <span>L'assuré social</span>
        <span dir="rtl">المضمون الاجتماعي</span>
      </div>
      <div className="bs-block">
        {[
          ["Prénom :", "firstName", ": الاسم"],
          ["Nom :", "lastName", ": اللقب"],
          ["Adresse :", "adresse", ": العنوان"],
          ["Code postal :", "codePostal", "الترقيم البريدي :"],
        ].map(([label, field, ar]) => (
          <div key={field} className="bs-line-row">
            <span className="bs-label">{label}</span>
            <input
              className="bs-field-input"
              value={assure[field]}
              onChange={(e) => setAssureField(field, e.target.value)}
            />
            <span className="bs-ar-label">{ar}</span>
          </div>
        ))}
        <div className="bs-line-row">
          <span className="bs-label">N° DU TEL PORTABLE</span>
          <input className="bs-phone-input" />
          <span className="bs-ar-label">رقم الهاتف الجوال</span>
        </div>
      </div>

      <div className="bs-section-title">
        <span>Le malade</span>
        <span dir="rtl">المريض</span>
      </div>
      <div className="bs-block">
        <div className="bs-patient-types">
          {[
            ["assure", "المضمون الاجتماعي", "L'assuré social"],
            ["conjoint", "القرين", "Le conjoint"],
            ["enfant", "الابن (*)", "L'enfant (*)"],
            ["ascendant", "الأصول (**)", "L'ascendant (**)"],
          ].map(([value, ar, fr]) => (
            <label key={value} className="bs-patient-type">
              <input
                type="radio"
                name="bs-malade-type"
                className="bs-checkbox"
                checked={maladeType === value}
                onChange={() => handleMaladeTypeChange(value)}
              />
              <div>
                <div dir="rtl" className="bs-pt-ar">{ar}</div>
                <div className="bs-pt-fr">{fr}</div>
              </div>
            </label>
          ))}
        </div>
        <div className="bs-note">
          (*) 1<sup>er</sup> Enfant = 01, 2<sup>ème</sup> = 02, 3<sup>ème</sup> = 03 | Le Père = 01, la Mère = 02
        </div>
        <div className="bs-line-row bs-suggestion-wrapper">
          <span className="bs-label">PRÉNOM :</span>
          <input
            className="bs-field-input"
            value={maladePrenom}
            onChange={(e) => { setMaladePrenom(e.target.value); setShowSuggestions(true); }}
            onFocus={() => setShowSuggestions(true)}
          />
          <span className="bs-ar-label">: الاسم</span>
          {showSuggestions && maladeType && maladeType !== "assure" && filteredDependents.length > 0 && (
            <div className="bs-suggestions-box">
              {filteredDependents.map((dep) => (
                <button key={dep.id} type="button" className="bs-suggestion-item"
                  onClick={() => fillMaladeFromDependent(dep)}>
                  {dep.nomPrenom} — {dep.dateNaissance ? String(dep.dateNaissance).slice(0, 10) : ""}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="bs-line-row">
          <span className="bs-label">NOM :</span>
          <input className="bs-field-input" value={maladeNom}
            onChange={(e) => { setMaladeNom(e.target.value); setShowSuggestions(true); }}
            onFocus={() => setShowSuggestions(true)} />
          <span className="bs-ar-label">: اللقب</span>
        </div>
        <div className="bs-line-row">
          <span className="bs-label">DATE DE NAISSANCE :</span>
          <input
            className="bs-field-input"
            value={maladeDateNaissance}
            placeholder="AAAA-MM-JJ"
            onChange={(e) => setMaladeDateNaissance(e.target.value)}
          />
          <span className="bs-ar-label">: تاريخ الولادة</span>
        </div>
      </div>

      <div className="bs-signature">
        <div className="bs-signature-title">
          <span>Signature de l'assuré</span>
          <span dir="rtl">إمضاء المضمون الاجتماعي</span>
        </div>
        <div className="bs-signature-box" />
      </div>
      <div className="bs-footer-note">
        <div><u>Très important :</u> Veuillez déposer ce formulaire au centre régional ou local le plus proche de votre domicile dans un délai ne dépassant pas les <u>60 jours</u> de la date des soins.</div>
        <div dir="rtl" className="bs-footer-ar">هام جداً : تسلم هذه البطاقة إلى أقرب مركز جهوي أو محلي لمقر إقامتكم خلال مدة لا تفوق <u>60 يوماً</u> من تاريخ العلاج.</div>
      </div>
    </div>
  );
}

/* ====== RECTO ====== */

function FaceRecto({ patientId, viewedActs = null }) {
  const dispatch = useDispatch();
  const { notification } = App.useApp();
  const { medicalActs, fetchActs, createAct, isCreating, isLoading } = useMedicalActs();
  const { patientConsultations, getByPatientId } = useConsultations();
  const doctor = useSelector((state) => state.doctor?.user || null);
  const doctorId = doctor?.pgId || doctor?.doctorId || doctor?.id || doctor?.raw?.pgId || doctor?.raw?.id || null;
  const doctorBsInfoKey = `bs_doctor_info_${doctorId || "unknown"}`;

  const getSavedDoctorBsInfo = () => {
    try { return JSON.parse(localStorage.getItem(doctorBsInfoKey) || "{}"); }
    catch { return {}; }
  };
  const saveDoctorBsInfo = (codeConventionnel, matriculeFiscal) => {
    localStorage.setItem(doctorBsInfoKey, JSON.stringify({ codeConventionnel, matriculeFiscal }));
  };

  const savedDoctorBsInfo = getSavedDoctorBsInfo();

  const createEmptyRow = () => ["", "", "", savedDoctorBsInfo.codeConventionnel || "", savedDoctorBsInfo.matriculeFiscal || "", ""];
  const createEmptyBioRow = () => ["", "", savedDoctorBsInfo.codeConventionnel || "", savedDoctorBsInfo.matriculeFiscal || "", ""];
  const createEmptyPharmaRow = () => ["", "", savedDoctorBsInfo.codeConventionnel || "", savedDoctorBsInfo.matriculeFiscal || "", ""];

  const [consultRows, setConsultRows] = useState(Array.from({ length: 3 }, () => createEmptyRow()));
  const [actRows, setActRows] = useState(Array.from({ length: 5 }, () => createEmptyRow()));
  const [bioRows, setBioRows] = useState(Array.from({ length: 4 }, () => createEmptyBioRow()));
  const [hospRows, setHospRows] = useState(Array.from({ length: 3 }, () => createEmptyRow()));
  const [pharmaRows, setPharmaRows] = useState(Array.from({ length: 3 }, () => createEmptyPharmaRow()));

  useEffect(() => {
    const info = getSavedDoctorBsInfo();
    const emptyRow = ["", "", "", info.codeConventionnel || "", info.matriculeFiscal || "", ""];

    if (Array.isArray(viewedActs)) {
      const consult = [], actesMed = [], hosp = [];
      viewedActs.forEach((act) => {
        const row = [
          act.date ? String(act.date).slice(0, 10) : "",
          "",
          act.honoraires != null ? Number(act.honoraires).toFixed(2) : "",
          act.codeConventionnel || info.codeConventionnel || "",
          act.matriculeFiscal || info.matriculeFiscal || "",
          "",
        ];
        if (act.type === "CONSULTATION_VISITE") consult.push(row);
        else if (act.type === "ACTE_MEDICAL") actesMed.push(row);
        else if (act.type === "ACCOUCHEMENT_HOSPITALISATION") hosp.push(row);
      });
      setConsultRows(Array.from({ length: 3 }, (_, i) => consult[i] || [...emptyRow]));
      setActRows(Array.from({ length: 5 }, (_, i) => actesMed[i] || [...emptyRow]));
      setHospRows(Array.from({ length: 3 }, (_, i) => hosp[i] || [...emptyRow]));
      return;
    }

    const sorted = [...(patientConsultations || [])].sort(
      (a, b) => new Date(b.consultationDateTime || b.createdAt || 0) - new Date(a.consultationDateTime || a.createdAt || 0)
    );
    const latest = sorted[0];
    if (!latest) return;

    const consultationDate = latest.consultationDateTime || latest.createdAt || "";
    const consultationRows = [];
    if (latest.consultationPrice != null && Number(latest.consultationPrice) > 0) {
      consultationRows.push([
        consultationDate ? String(consultationDate).slice(0, 10) : "",
        "", Number(latest.consultationPrice).toFixed(2),
        info.codeConventionnel || "", info.matriculeFiscal || "", "",
      ]);
    }

    const acts = latest.medicalActs || latest.medicalActsPerformed || latest.acts || latest.actes || [];
    const actRowsFromConsultation = acts.map((act) => {
      const key = Number(act?.key ?? 1);
      const cotation = Number(act?.cotation ?? 0);
      const honoraires = Number(act?.honoraires || 0) || Number(act?.amount || 0) || Number(act?.total || 0) || Number(act?.price || 0) || key * cotation;
      return [consultationDate ? String(consultationDate).slice(0, 10) : "", act?.code || "", honoraires ? honoraires.toFixed(2) : "", info.codeConventionnel || "", info.matriculeFiscal || "", ""];
    });

    setConsultRows(Array.from({ length: 3 }, (_, i) => consultationRows[i] ? consultationRows[i] : [...emptyRow]));
    setActRows(Array.from({ length: 5 }, (_, i) => actRowsFromConsultation[i] ? actRowsFromConsultation[i] : [...emptyRow]));
  }, [patientConsultations, viewedActs]);

  useEffect(() => { if (patientId) getByPatientId(patientId); }, [patientId, getByPatientId]);

  useEffect(() => {
    const refresh = () => { if (patientId) getByPatientId(patientId); };
    window.addEventListener("consultations-updated", refresh);
    window.addEventListener("focus", refresh);
    return () => { window.removeEventListener("consultations-updated", refresh); window.removeEventListener("focus", refresh); };
  }, [patientId, getByPatientId]);

  useEffect(() => { if (!doctorId) dispatch(getCurrentDoctor()); }, [dispatch, doctorId]);

  const savedRef = useRef(false);
  const filledRef = useRef(false);

  useEffect(() => {
    if (savedRef.current || filledRef.current) return;
    if (!patientConsultations || patientConsultations.length === 0) return;
    filledRef.current = true;
    const info = getSavedDoctorBsInfo();
    const emptyRow = ["", "", "", info.codeConventionnel || "", info.matriculeFiscal || "", ""];
    const sorted = [...patientConsultations].sort(
      (a, b) => new Date(b.consultationDateTime || b.createdAt || 0) - new Date(a.consultationDateTime || a.createdAt || 0)
    );
    const latest = sorted[0];
    if (!latest) return;
    const dateStr = (latest.consultationDateTime || latest.createdAt || "").slice(0, 10);
    const actMedicalRows = (latest.medicalActs || []).map((act) => [
      dateStr, act?.designation || act?.code || "",
      act?.price != null ? Number(act.price).toFixed(2) : "",
      info.codeConventionnel || "", info.matriculeFiscal || "", "",
    ]);
    setActRows(Array.from({ length: 5 }, (_, i) => actMedicalRows[i] ? actMedicalRows[i] : [...emptyRow]));
  }, [patientConsultations]);

  const saveDoctorInfoFromRow = (row) => {
    const codeConventionnel = String(row[3] || "").trim();
    const matriculeFiscal = String(row[4] || "").trim();
    if (!codeConventionnel || !matriculeFiscal) return;
    // memorise pour prefixer le prochain BS, sans jamais figer la saisie
    saveDoctorBsInfo(codeConventionnel, matriculeFiscal);
  };

  const handleConsultChange = (r, c, value) => setConsultRows((rows) => rows.map((row, i) => { if (i !== r) return row; const updated = row.map((cell, j) => (j === c ? value : cell)); if (c === 3 || c === 4) saveDoctorInfoFromRow(updated); return updated; }));
  const handleActChange = (r, c, value) => setActRows((rows) => rows.map((row, i) => { if (i !== r) return row; const updated = row.map((cell, j) => (j === c ? value : cell)); if (c === 3 || c === 4) saveDoctorInfoFromRow(updated); return updated; }));
  const handleBioChange = (r, c, value) => setBioRows((rows) => rows.map((row, i) => i === r ? row.map((cell, j) => (j === c ? value : cell)) : row));
  const handleHospChange = (r, c, value) => setHospRows((rows) => rows.map((row, i) => { if (i !== r) return row; const updated = row.map((cell, j) => (j === c ? value : cell)); if (c === 3 || c === 4) saveDoctorInfoFromRow(updated); return updated; }));
  const handlePharmaChange = (r, c, value) => setPharmaRows((rows) => rows.map((row, i) => i === r ? row.map((cell, j) => (j === c ? value : cell)) : row));

  const toNumber = (value) => { const n = Number(String(value || "").replace(",", ".")); return Number.isNaN(n) ? null : n; };

  const handleSaveConsommation = useCallback(async () => {
    if (!doctorId || !patientId) { notification.warning({ message: "Impossible d'enregistrer", description: "Médecin ou patient introuvable dans le contexte." }); return; }
    const actsPayload = [];
    const normalizeRow = (row) => row.map((v) => String(v || "").trim());
    const isEmptyRow = (row) => row.every((v) => !String(v || "").trim());
    const pushIfValid = (type, row) => {
      const cleanRow = normalizeRow(row);
      if (isEmptyRow(cleanRow)) return;
      const [date, designation, honoraires, codeConventionnel, matriculeFiscal] = cleanRow;
      const honorairesNumber = toNumber(honoraires);
      if (!date || !designation || honorairesNumber === null || !codeConventionnel || !matriculeFiscal) return;
      actsPayload.push({ type, date, designation, honoraires: honorairesNumber, codeConventionnel, matriculeFiscal });
    };
    consultRows.forEach((row) => pushIfValid("CONSULTATION_VISITE", row));
    actRows.forEach((row) => pushIfValid("ACTE_MEDICAL", row));
    hospRows.forEach((row) => pushIfValid("ACCOUCHEMENT_HOSPITALISATION", row));
    if (!actsPayload.length) { notification.info({ message: "Aucun acte valide", description: "Veuillez remplir tous les champs obligatoires." }); return; }
    try {
      for (const act of actsPayload) await createAct(doctorId, patientId, act).unwrap();
      await fetchActs(patientId);
      window.dispatchEvent(new CustomEvent("cnss-medical-acts-updated", { detail: { patientId } }));
      notification.success({ message: "Consommation BS enregistrée", description: `${actsPayload.length} acte(s) enregistré(s).` });
      savedRef.current = true;
      setConsultRows((rows) => rows.map((row) => row.map((cell, i) => (i === 0 || i === 2 ? "" : cell))));
      setActRows((rows) => rows.map((row) => row.map((cell, i) => (i === 0 || i === 2 ? "" : cell))));
      setHospRows((rows) => rows.map((row) => row.map((cell, i) => (i === 0 || i === 2 ? "" : cell))));
      setBioRows((rows) => rows.map((row) => row.map((cell, i) => (i === 0 || i === 1 ? "" : cell))));
      setPharmaRows((rows) => rows.map((row) => row.map((cell, i) => (i === 0 || i === 1 ? "" : cell))));
    } catch (err) {
      notification.error({ message: "Erreur lors de l'enregistrement", description: err?.response?.data?.message?.join?.(", ") || err?.message || "Erreur inconnue" });
    }
  }, [doctorId, patientId, consultRows, actRows, hospRows, createAct, fetchActs, notification]);

  useEffect(() => {
    const onSave = () => handleSaveConsommation();
    window.addEventListener("save-bs-consommation", onSave);
    return () => window.removeEventListener("save-bs-consommation", onSave);
  }, [handleSaveConsommation]);

  useEffect(() => {
    const onViewActs = (e) => {
      const pid = String(e?.detail?.patientId ?? "");
      if (!pid || pid !== String(patientId)) return;
      const acts = e?.detail?.acts;
      if (!acts) { setConsultRows(Array.from({ length: 3 }, () => createEmptyRow())); setActRows(Array.from({ length: 5 }, () => createEmptyRow())); setHospRows(Array.from({ length: 3 }, () => createEmptyRow())); return; }
      const info = getSavedDoctorBsInfo();
      const consult = [], actesMed = [], hosp = [];
      for (const a of acts) {
        const row = [a.date ? String(a.date).slice(0, 10) : "", a.designation || "", a.honoraires != null ? Number(a.honoraires).toFixed(2) : "", a.codeConventionnel || info.codeConventionnel || "", a.matriculeFiscal || info.matriculeFiscal || "", ""];
        if (a.type === "CONSULTATION_VISITE") consult.push(row);
        else if (a.type === "ACTE_MEDICAL") actesMed.push(row);
        else if (a.type === "ACCOUCHEMENT_HOSPITALISATION") hosp.push(row);
      }
      setConsultRows(Array.from({ length: 3 }, (_, i) => consult[i] ? consult[i] : createEmptyRow()));
      setActRows(Array.from({ length: 5 }, (_, i) => actesMed[i] ? actesMed[i] : createEmptyRow()));
      setHospRows(Array.from({ length: 3 }, (_, i) => hosp[i] ? hosp[i] : createEmptyRow()));
    };
    window.addEventListener("view-bs-acts", onViewActs);
    return () => window.removeEventListener("view-bs-acts", onViewActs);
  }, [patientId]);

  return (
    <div className="recto-page">
      <div className="recto-main-title">A REMPLIR PAR LES PROFESSIONNELS DE SANTÉ</div>
      {isLoading && <div style={{ marginBottom: 10, fontSize: 13 }}>Chargement des actes enregistrés...</div>}
      <div className="recto-top-box">
        <div className="recto-care-line">
          <span className="recto-care-title">Soins effectuée ou Prescrits dans le cadre de :</span>
          {["APCI", "MO", "Hospitalisation", "Suivi de Grossesse"].map((item) => (
            <label key={item} className="recto-check-item">
              <span>{item}</span>
              <input type="checkbox" />
            </label>
          ))}
        </div>
        <div className="recto-code-row">
          <span>code APCI</span>
          <input className="recto-small-input" />
          <input className="recto-small-input" />
          <span className="recto-date-label">Date prévue d'accouchement</span>
          <input className="recto-date-box" /><span>/</span>
          <input className="recto-date-box" /><span>/</span>
          <input className="recto-date-box recto-year" />
        </div>
        <div className="recto-malade-row">
          <span>NOM ET PRÉNOM DU MALADE :</span>
          <input />
        </div>
      </div>

      {/* Consultations & Visites */}
      <div className="recto-section">
        <div className="recto-side-label"><span>Consultation</span><span>& Visites</span></div>
        <div className="recto-section-content">
          <table className="recto-table">
            <thead><tr><th>DATE</th><th>DÉSIGNATION</th><th>HONORAIRES</th><th>CODE CONVENTIONNEL</th><th>MATRICULE FISCAL</th><th>CACHET & SIGNATURE</th></tr></thead>
            <tbody><RectoEditableRows rows={consultRows} onChange={handleConsultChange} /></tbody>
          </table>
        </div>
      </div>
      <div className="recto-note">Joindre obligatoirement la prescription précisant l'acte effectué</div>

      {/* Actes Médicaux */}
      <div className="recto-section">
        <div className="recto-side-label"><span>Actes Médicaux</span><span>& Paramédicaux</span></div>
        <div className="recto-section-content">
          <table className="recto-table">
            <thead><tr><th>DATE</th><th>DÉSIGNATION</th><th>HONORAIRES</th><th>CODE CONVENTIONNEL</th><th>MATRICULE FISCAL</th><th>CACHET & SIGNATURE</th></tr></thead>
            <tbody><RectoEditableRows rows={actRows} onChange={handleActChange} /></tbody>
          </table>
        </div>
      </div>

      {/* Biologie */}
      <div className="recto-section">
        <div className="recto-side-label"><span>Biologie</span></div>
        <div className="recto-section-content">
          <div className="recto-note">Joindre obligatoirement les codes des actes</div>
          <table className="recto-table">
            <thead><tr><th>DATE</th><th>MONTANT</th><th>CODE CONVENTIONNEL</th><th>MATRICULE FISCAL</th><th>CACHET & SIGNATURE</th></tr></thead>
            <tbody><RectoEditableRows rows={bioRows} onChange={handleBioChange} /></tbody>
          </table>
        </div>
      </div>

      {/* Accouchement / Hospitalisation */}
      <div className="recto-section">
        <div className="recto-side-label"><span>Accouchement</span><span>Hospitalisation</span></div>
        <div className="recto-section-content">
          <div className="recto-note">*En cas d'accouchement joindre obligatoirement l'extrait de naissance du nouveau né</div>
          <table className="recto-table">
            <thead><tr><th>DATE</th><th>DÉSIGNATION</th><th>HONORAIRES</th><th>CODE CONVENTIONNEL</th><th>MATRICULE FISCAL</th><th>CACHET & SIGNATURE</th></tr></thead>
            <tbody><RectoEditableRows rows={hospRows} onChange={handleHospChange} /></tbody>
          </table>
        </div>
      </div>

      {/* Pharmacie */}
      <div className="recto-section">
        <div className="recto-side-label"><span>Pharmacie</span></div>
        <div className="recto-section-content">
          <table className="recto-table">
            <thead><tr><th>DATE DE DISPENSATION</th><th>MONTANT</th><th>CODE CONVENTIONNEL</th><th>MATRICULE FISCAL</th><th>CACHET & SIGNATURE</th></tr></thead>
            <tbody><RectoEditableRows rows={pharmaRows} onChange={handlePharmaChange} /></tbody>
          </table>
        </div>
      </div>

      <div className="recto-vignettes"><span>COLLER ICI LES VIGNETTES</span></div>
      <div className="recto-footer-title"><span>Avis important</span><span dir="rtl">هام جدا</span></div>
      <div className="recto-footer">
        <div dir="rtl">يجب تحرير هذه المطبوعة بكل دقة، ولا يمكن استعمالها إلا لمريض واحد.</div>
        <div>Ce bulletin de soins doit être rempli avec la plus grande précision, il ne peut servir que pour un seul malade.</div>
      </div>
    </div>
  );
}

/* ====== MODAL CHOIX IMPRESSION ====== */

function PrintModal({ onClose, onPrint }) {
  return (
    <div className="bs-modal-overlay" onClick={onClose}>
      <div className="bs-modal" onClick={(e) => e.stopPropagation()}>
        <div className="bs-modal-header">
          <span>Choisir le mode d'impression</span>
          <button type="button" className="bs-modal-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="bs-modal-body">
          <button
            type="button"
            className="bs-modal-option"
            onClick={() => onPrint(true)}
          >
            <div className="bs-modal-option-icon"></div>
            <div className="bs-modal-option-content">
              <div className="bs-modal-option-title">Avec Template</div>
              <div className="bs-modal-option-desc">
                Imprime le formulaire CNAM complet en A4 avec couleurs,
                bordures, logo et en-têtes.
              </div>
            </div>
          </button>

          <button
            type="button"
            className="bs-modal-option"
            onClick={() => onPrint(false)}
          >
            <div className="bs-modal-option-icon">📄</div>
            <div className="bs-modal-option-content">
              <div className="bs-modal-option-title">Sans Template</div>
              <div className="bs-modal-option-desc">
                Imprime uniquement les données saisies sur fond blanc,
                pour formulaire CNAM pré-imprimé.
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}

/* ====== TOGGLE BAR ====== */

function ToggleBar({ side, setSide, onPrintClick }) {
  return (
    <div className="bs-toggle-bar">
      <div className="bs-pill">
        <button
          type="button"
          className={`bs-toggle-btn ${side === "verso" ? "active" : ""}`}
          onClick={() => setSide("verso")}
        >
          VERSO
        </button>

        <button
          type="button"
          className={`bs-toggle-btn ${side === "recto" ? "active" : ""}`}
          onClick={() => setSide("recto")}
        >
          RECTO
        </button>
      </div>

      <button type="button" className="bs-print-btn" onClick={onPrintClick}>
        Imprimer
      </button>
    </div>
  );
}

/* ====== COMPOSANT PRINCIPAL ====== */

export default function BulletinDeSoins({ card, patientId, viewedActs = null }) {
  const [side, setSide] = useState("verso");
  const [showPrintModal, setShowPrintModal] = useState(false);

  const printPortalRef = useRef(null);

  const syncInputValues = (root) => {
    if (!root) return;
    root.querySelectorAll("input, textarea").forEach((el) => {
      if (el.type === "checkbox" || el.type === "radio") {
        if (el.checked) el.setAttribute("checked", "checked");
        else el.removeAttribute("checked");
        return;
      }
      el.setAttribute("value", el.value ?? "");
    });
  };

  const buildPrintClone = (sourceEl, pageClass) => {
    if (!sourceEl) return null;
    syncInputValues(sourceEl);
    const clone = sourceEl.cloneNode(true);
    clone.removeAttribute("style");
    clone.classList.add(pageClass);
    return clone;
  };

  const handlePrint = (withTemplate) => {
    setShowPrintModal(false);

    setTimeout(() => {
      const portal = printPortalRef.current;
      if (!portal) return;

      const versoSource = document.querySelector(".bs-sheet:not(.bs-sheet-recto)");
      const rectoSource = document.querySelector(".bs-sheet-recto");

      const versoClone = buildPrintClone(versoSource, "bs-print-page-verso");
      const rectoClone = buildPrintClone(rectoSource, "bs-print-page-recto");

      portal.innerHTML = "";

      // PAGE 1 - VERSO
      if (versoClone) {
        const page = document.createElement("div");
        page.className = "bs-print-page bs-print-page-verso-outer";
        page.appendChild(versoClone);
        portal.appendChild(page);
      }

      // PAGE 2 - RECTO
      if (rectoClone) {
        const page = document.createElement("div");
        page.className = "bs-print-page bs-print-page-recto";
        page.appendChild(rectoClone);
        portal.appendChild(page);
      }

      portal.classList.toggle("bs-print-no-template", !withTemplate);

      const cleanup = () => {
        portal.innerHTML = "";
        portal.classList.remove("bs-print-no-template");
        window.removeEventListener("afterprint", cleanup);
      };

      window.addEventListener("afterprint", cleanup);
      window.print();

      setTimeout(cleanup, 4000);
    }, 150);
  };

  return (
    <div className="bs-root">
      <ToggleBar
        side={side}
        setSide={setSide}
        onPrintClick={() => setShowPrintModal(true)}
      />

      {showPrintModal && (
        <PrintModal
          onClose={() => setShowPrintModal(false)}
          onPrint={handlePrint}
        />
      )}

      {/* VERSO */}
      <div
        className="bs-sheet"
        style={{ display: side === "verso" ? "flex" : "none" }}
      >
        <div className="bs-col bs-col-gauche">
          <FaceGauche />
        </div>

        <div className="bs-divider" />

        <div className="bs-col bs-col-droite">
          <FaceDroite card={card} patientId={patientId} />
        </div>
      </div>

      {/* RECTO */}
      <div
        className="bs-sheet bs-sheet-recto"
        style={{ display: side === "recto" ? "block" : "none" }}
      >
        <FaceRecto patientId={patientId} viewedActs={viewedActs} />
      </div>

      <div className="bs-save-bar">
        <button
          type="button"
          className="bs-save-btn"
          onClick={() =>
            window.dispatchEvent(new CustomEvent("save-bs-consommation"))
          }
        >
          Enregistrer BS
        </button>
      </div>

      {typeof document !== "undefined" &&
        createPortal(<div id="bs-print-portal" ref={printPortalRef} />, document.body)}
    </div>
  );
}